/**
 * LibraryContent entity class
 */

import moment from 'moment'

/**
 * LibraryContentFactory
 * returns VLibraryContentOD entity class
 *
 * @class LibraryContentFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.LibraryContent} class
 *
 * @ngdoc factory
 * @name mwApp.factory:LibraryContentFactory
 */
/* @ngInject */
export default function LibraryContentFactory ($q, $http, $injector, Entity, VODSet, Network, Type, m2mMixin, onOffMixin, accessParser, DefaultStream, UrlPolicy, alertLiteSvc, authSvc) {
  /* === LibraryContent class === */

  /**
   * LibraryContent model
   *
   * @class LibraryContent
   * @classdesc LibraryContent model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.LibraryContent} LibraryContent entity
   */
  function LibraryContent () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  LibraryContent.prototype = Object.create(Entity.prototype)
  LibraryContent.prototype.constructor = LibraryContent

  /* === LibraryContent instance methods & fields === */

  Object.assign(LibraryContent.prototype, {

    /**
     * instance fields
     *
     * @memberof models.LibraryContent#
     * @type {Object}
     */
    _fields: {
      activeFrom: moment,
      activeUntil: moment,
      autoPay: Boolean,
      contentId: Number,
      contentName: String,
      contentType: String,
      monetizationType: String,
      networkId: Number,
      purchaseType: String,
      seasonId: Number,
      seasonName: String,
      subscriber: 'Subscriber',
      tvShowId: Number,
      tvShowName: String
    },

    /**
     * url template
     *
     * @memberof models.LibraryContent#
     * @type {String}
     */
    _urlTemplate: 'api/subscribers/{{::subscriber.id}}/library',

    /**
     * public url template
     *
     * @memberof models.LibraryContent#
     * @type {String}
     */
    _publicUrlTemplate: 'subscribers/{{::subscriber.id}}/library',

    /**
     * Prepare LibraryContent
     *
     * @memberof models.LibraryContent#
     * @override
     */
    // _prepare: function () {
    //
    // },

    /**
     * Get underlying object
     *
     * @memberof models.LibraryContent#
     * @return {Object} new underlying object as plain object
     * @override
     */
    // _object: function () {
    //   return Entity.prototype._object.apply(this, arguments)
    // },

    /**
     * Get image URL for given size
     *
     * @memberof models.LibraryContent#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}')
          ? this[fieldName].replace('{{size}}', size)
          : this[fieldName] + '?resize=' + size
        : ''
    },
    /**
     * On/Off Autopay for subscriber, performing PATCH request to the server
     *
     * @return {Promise} promise
     */
    _onOffAutopay: function () {
      const autoPay = this.autoPay
      const url = `${this.$_urlTemplate(this)}/${this.contentType}/${this.contentId}/autopay`

      if (typeof autoPay !== 'boolean') {
        return
      }

      return $http.patch(url, autoPay ? '"ENABLE_AUTOPAY"' : '"DISABLE_AUTOPAY"')
        .catch(r => {
          this.autoPay = !this.autoPay
          alertLiteSvc.alertError(r)
        })
    },

    /**
     * Delete entity, performing DELETE request to backend
     *
     * @memberof models.Entity#
     * @param  {String}  [msg] - alerted message after delete is successful (if 'null' -> there will be no message)
     * @param  {String}  [msg] - alerted message after delete is successful (if 'null' -> there will be no message)
     * @return {Promise} promise
     */
    _delete: function (msg) {
      var name = this.name ? this.name + ' ' : ''
      var cls = this.constructor
      var cName = cls._publicName || cls._name

      if (msg !== null) {
        msg = msg || name + cName + ' deleted'
      }

      // if there is access defined in entity class -> check 'delete' access
      if (cls.access && cls.access.delete && !accessParser(cls.access.delete).check()) {
        // return error in case user doesn't have 'delete' access
        return $q.reject('You have no permissions to delete ' + cName)
          .catch(alertLiteSvc.alertError)
      }
      const url = this.$_urlTemplate(this)

      return $http.delete(`${url}/${this.contentType}/${this.contentId}`)
        .then(alertLiteSvc.alertSuccess(msg, 'deleted'))
        .catch(alertLiteSvc.alertError)
    }

  }, onOffMixin)

  /* === LibraryContent static methods & fields === */

  LibraryContent.loadContent = function (tableState, o) {
    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = `${cast.prototype.$_urlTemplate(o)}`
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    manageActiveRange(tableState, ['avail-from-before', 'avail-from-after'], 'activeFrom')
    manageActiveRange(tableState, ['avail-to-before', 'avail-to-after'], 'activeUntil')

    return LibraryContent.loadSmart(tableState, o, `${url}?content-type=${o.contentType}`)
  }

  function manageActiveRange (tableState, keysRange, key) {
    const predicateObject = tableState.search.predicateObject || {}

    if (Object.prototype.hasOwnProperty.call(predicateObject, key)) {
      const active = JSON.parse(predicateObject[key])
      if (active.before) predicateObject[keysRange[0]] = moment(active.before).format('YYYY-MM-DDTHH:mm:ss[Z]')
      if (active.after) predicateObject[keysRange[1]] = moment(active.after).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (!active.before && predicateObject[keysRange[0]]) delete predicateObject[keysRange[0]]
      if (!active.after && predicateObject[keysRange[1]]) delete predicateObject[keysRange[1]]

      // delete predicateObject[key]
    } else {
      if (predicateObject[keysRange[0]]) delete predicateObject[keysRange[0]]
      if (predicateObject[keysRange[1]]) delete predicateObject[keysRange[1]]
    }
  }

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  LibraryContent.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Search tags by param
   *
   * @param {String} url
   * @param {Object} params
   * @returns {Promise} promise
   */
  LibraryContent.search = function (url, params) {
    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(LibraryContent, Entity)
  LibraryContent._name = 'LibraryContent'
  LibraryContent.init()

  // permissions
  LibraryContent.access = {
    view: 'viewSubscriberLibrary viewSubscriptionPayment &',
    create: 'editSubscriberLibrary',
    edit: 'editSubscriberLibrary',
    delete: 'editSubscriberLibrary'
  }

  return LibraryContent
}
